import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui';

interface AccessDeniedProps {
  /** Optional human-readable name of what was being accessed, e.g. "Users". */
  what?: string;
}

/**
 * Shown when an authenticated user reaches a screen their role does not grant.
 *
 * Deliberately explicit rather than a silent redirect. The existing route guards bounce to
 * /home without explanation, which is confusing when the user followed a link or a bookmark -
 * it looks like the app lost their click. Telling them plainly that the screen exists but their
 * role does not include it is both less alarming and more actionable.
 *
 * This is not a security control. The server refuses the underlying requests regardless; this
 * only decides what the user sees instead of a broken page.
 */
const AccessDenied: React.FC<AccessDeniedProps> = ({ what }) => (
  <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-px-4 tw-text-center">
    <h4 className="tw-mb-2">You don’t have access to {what ?? 'this page'}</h4>
    <p className="tw-text-content-muted tw-text-sm tw-mb-4 tw-max-w-md">
      Your role doesn’t include permission for this screen. If you think you should have it,
      ask an administrator to review your role.
    </p>
    <Button as={Link} to="/home" variant="primary">
      Go to Home
    </Button>
  </div>
);

export default AccessDenied;
